// EntryTipModal — the popup that opens when the user taps the `?`
// pill on a Logger entry. Replaces the 1.67.5 inline-description
// toggle with a richer surface: description + dual worked-muscles
// bodymap + ranked alternative exercises (tap an alt to swap).
//
// Design picks come from feedback_quick_tip_pill_layout (memory):
// vertical layout description → dual bodymap (front+back side-by-
// side simultaneously) → alternatives list. Decided 2026-05-19.
//
// Composition over creation: this Modal composes existing pieces.
// Description renderer = pickDescription() from @jackedlogs/shared.
// Dual bodymap = the existing <BodyMap layout='dual'> that 1.66.0
// WorkoutBodyMap already uses. Alternatives ranking =
// rankSwapCandidates() that ExerciseSwapPicker already uses. The
// parent owns the swap-action wiring (loadedSides + isCardio
// branching, sticky bilateral preference) so swap behaviour stays
// identical to the dedicated swap picker.

import { useMemo } from 'react';
import {
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import {
  PAIN_SEVERITY_COLOURS,
  WORKED_MUSCLE_COLOURS,
  exercisePainSeverity,
  pickDescription,
  rankSwapCandidates,
  workedMusclesFromExercises,
  type Exercise,
  type Locale,
  type MuscleSlug,
} from '@jackedlogs/shared';

import { ScaleLegend } from '../index';

import { BodyMap } from '../bodymap/BodyMap';
import { ExerciseVisualPager, type AnimationSourceFn } from './ExerciseVisualPager';
import { showsPerImplementHint } from './loadingHint';
import { useExerciseName } from './exerciseName';
import { useTheme } from './theme';

// Top-N alternatives to surface. rankSwapCandidates returns the full
// sorted list; we cap to keep the popup readable on phone screens.
// User can still open the dedicated swap picker (⇄ icon) for the
// full list when none of these match.
const ALT_LIMIT = 6;

const SUPPORTED_LOCALES: ReadonlyArray<Locale> = ['en', 'it', 'fr', 'de', 'ru'];

// Canonical muscle slug → display label. Slugs are domain-stable English
// (kebab-case). For v1 we surface them lightly formatted; a future ship
// can wire localized labels through a canonical → catalog → muscleName
// inverse map if Antenore asks.
const formatMuscleSlug = (slug: MuscleSlug): string =>
  slug.replace(/-/g, ' ');

const normaliseLocale = (lang: string | undefined): Locale => {
  const base = (lang ?? 'en').split('-')[0] ?? 'en';
  return (SUPPORTED_LOCALES as readonly string[]).includes(base)
    ? (base as Locale)
    : 'en';
};

export type EntryTipModalProps = {
  visible: boolean;
  exercise: Exercise | null;
  catalog: ReadonlyArray<Exercise> | null;
  /** Per-exercise program/plan instruction (DraftEntry.notes — carries
   *  Slot.instructions for program sessions). Surfaced read-only here so
   *  the coach's "how to do this exercise in this program" note is finally
   *  visible at log time (it isn't shown anywhere else). Empty = absent. */
  planNote?: string;
  onClose: () => void;
  /** Parent owns the swap mechanics (bilateral preference reload,
   *  cardio branching) so we delegate the actual mutation. */
  onPickAlternative: (alt: Exercise) => void;
  profile: import('./hostDeps').LoggerProfile;
  PainDot?: import('react').ComponentType<{ exercise: Exercise }>;
  /** AN-2 — host resolver for the exercise animation. Absent, or answering
   *  null, means this exercise has no GIF, which is the normal case for most
   *  of the catalog: the band below then shows the bodymap alone, exactly as
   *  it did before the pager existed. */
  animationSource?: AnimationSourceFn;
};

export function EntryTipModal({
  visible,
  exercise,
  catalog,
  planNote,
  onClose,
  onPickAlternative,
  profile,
  PainDot,
  animationSource,
}: EntryTipModalProps) {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const getExerciseName = useExerciseName();
  const styles = makeStyles(theme);

  const locale = normaliseLocale(i18n.resolvedLanguage ?? i18n.language);

  const description = useMemo(
    () => (exercise ? pickDescription(exercise.description, locale) : null),
    [exercise, locale],
  );

  const bodyValue = useMemo(() => {
    if (!exercise) return {};
    return workedMusclesFromExercises([
      { primary: exercise.primary, secondary: exercise.secondary },
    ]);
  }, [exercise]);

  const alternatives = useMemo(() => {
    if (!exercise || !catalog) return [];
    return rankSwapCandidates(exercise, catalog).slice(0, ALT_LIMIT);
  }, [exercise, catalog]);

  const painRisk = useMemo(
    () => (exercise ? exercisePainSeverity(exercise, profile.painMap) : undefined),
    [exercise, profile.painMap],
  );

  return (
    <Modal
      visible={visible}
      animationType="slide"
      onRequestClose={onClose}
      presentationStyle="pageSheet"
    >
      <SafeAreaView
        style={[styles.safe, { backgroundColor: theme.palette.bg }]}
        edges={['top', 'left', 'right', 'bottom']}
      >
        <View style={styles.header}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t('workout.tipClose', {
              defaultValue: 'Close',
            })}
            onPress={onClose}
            hitSlop={8}
          >
            <Text style={styles.close}>×</Text>
          </Pressable>
          <Text style={styles.title} numberOfLines={1}>
            {exercise ? getExerciseName(exercise.name) : ''}
          </Text>
          <View style={styles.headerSpacer} />
        </View>

        <ScrollView
          contentContainerStyle={styles.body}
          keyboardShouldPersistTaps="handled"
        >
          {painRisk ? (
            <View
              style={[
                styles.painBanner,
                {
                  backgroundColor: PAIN_SEVERITY_COLOURS[painRisk.severity],
                },
              ]}
            >
              <Text style={styles.painBannerTitle}>
                {t(`workout.painBanner.${painRisk.severity}`)}
              </Text>
              <Text style={styles.painBannerBody}>
                {painRisk.painfulMuscles
                  .map((m) => formatMuscleSlug(m.slug))
                  .join(', ')}
              </Text>
            </View>
          ) : null}

          {exercise?.pinnedNote ? (
            <View style={styles.noteBlock}>
              <Text style={styles.noteBlockLabel}>
                {t('workout.tipCoachNote', { defaultValue: 'Coach note' })}
              </Text>
              <Text style={styles.noteBlockText}>{exercise.pinnedNote}</Text>
            </View>
          ) : null}

          {planNote && planNote.trim().length > 0 ? (
            <View style={styles.noteBlock}>
              <Text style={styles.noteBlockLabel}>
                {t('workout.tipProgramNote', {
                  defaultValue: 'In this program',
                })}
              </Text>
              <Text style={styles.noteBlockText}>{planNote}</Text>
            </View>
          ) : null}

          {description ? (
            <Text style={styles.description}>{description}</Text>
          ) : null}

          {exercise && showsPerImplementHint(exercise) ? (
            <View style={styles.loadingTip}>
              <Text style={styles.loadingTipText}>
                {t('workout.perImplementHint', {
                  defaultValue:
                    'Using two, or one side at a time? Log the weight of just one. The app counts both (×2).',
                })}
              </Text>
            </View>
          ) : null}

          {Object.keys(bodyValue).length > 0 ? (
            <View style={styles.bodySection}>
              <ExerciseVisualPager
                slug={exercise?.slug ?? ''}
                exerciseName={exercise ? getExerciseName(exercise.name) : ''}
                animationSource={exercise ? animationSource : undefined}
              >
                <View style={styles.bodyMapWrap}>
                  <BodyMap
                    mode="display"
                    view="front"
                    value={bodyValue}
                    scale={1.2}
                    figure={profile.preferredFigure}
                    layout="dual"
                    theme={theme}
                  />
                </View>
              </ExerciseVisualPager>
              <ScaleLegend
                variant="dots"
                theme={theme}
                style={styles.legendRow}
                items={[
                  { color: WORKED_MUSCLE_COLOURS.primary, label: t('bodymap.workedPrimary') },
                  { color: WORKED_MUSCLE_COLOURS.secondary, label: t('bodymap.workedSecondary') },
                ]}
              />
            </View>
          ) : null}

          <View style={styles.altsSection}>
            <Text style={styles.altsLabel}>
              {t('workout.tipAlternatives')}
            </Text>
            {alternatives.length === 0 ? (
              <Text style={styles.altsEmpty}>
                {t('workout.tipNoAlternatives')}
              </Text>
            ) : (
              alternatives.map((cand) => (
                <Pressable
                  key={cand.exercise.slug}
                  accessibilityRole="button"
                  onPress={() => onPickAlternative(cand.exercise)}
                  style={({ pressed }) => [
                    styles.altRow,
                    pressed && styles.altRowPressed,
                  ]}
                >
                  <View style={styles.altLabel}>
                    {PainDot ? <PainDot exercise={cand.exercise} /> : null}
                    <Text style={styles.altName} numberOfLines={1}>
                      {getExerciseName(cand.exercise.name)}
                    </Text>
                  </View>
                  <Text style={styles.altChevron}>›</Text>
                </Pressable>
              ))
            )}
          </View>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    safe: { flex: 1 },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: s.s5,
      paddingVertical: s.s3,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: p.border,
    },
    close: {
      fontSize: 28,
      lineHeight: 28,
      color: p.ink2,
      paddingHorizontal: s.s2,
      minWidth: 32,
      textAlign: 'left',
    },
    title: {
      flex: 1,
      textAlign: 'center',
      color: p.ink,
      fontSize: ty.sizes.md,
      fontWeight: '600',
      paddingHorizontal: s.s3,
    },
    headerSpacer: { width: 32 },
    body: {
      padding: s.s5,
      gap: s.s5,
    },
    painBanner: {
      borderRadius: r.r3,
      paddingHorizontal: s.s4,
      paddingVertical: s.s3,
      gap: s.s1,
    },
    painBannerTitle: {
      color: '#ffffff',
      fontSize: ty.sizes.sm,
      fontWeight: '700',
      letterSpacing: 0.4,
      textTransform: 'uppercase',
    },
    painBannerBody: {
      color: '#ffffff',
      fontSize: ty.sizes.sm,
      fontWeight: '500',
      textTransform: 'capitalize',
    },
    description: {
      color: p.ink,
      fontSize: ty.sizes.sm,
      lineHeight: ty.sizes.sm * ty.leading.base,
    },
    // Coach / program note blocks — brand left-rail to read as authored
    // guidance, distinct from the catalog description below.
    noteBlock: {
      backgroundColor: p.surface2,
      borderRadius: r.r2,
      borderLeftWidth: 3,
      borderLeftColor: p.brand500,
      paddingHorizontal: s.s4,
      paddingVertical: s.s3,
      gap: s.s1,
    },
    noteBlockLabel: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontWeight: '800',
      letterSpacing: 0.5,
      textTransform: 'uppercase',
    },
    noteBlockText: {
      color: p.ink,
      fontSize: ty.sizes.base,
      lineHeight: ty.sizes.base * ty.leading.base,
    },
    // Per-implement logging tip — a subtle info card (not the brand note rail,
    // which is reserved for coach/program guidance). Readable body size.
    loadingTip: {
      backgroundColor: p.surface2,
      borderRadius: r.r2,
      paddingHorizontal: s.s4,
      paddingVertical: s.s3,
    },
    loadingTipText: {
      color: p.ink2,
      fontSize: ty.sizes.sm,
      lineHeight: ty.sizes.sm * ty.leading.base,
    },
    bodySection: { gap: s.s3 },
    legendRow: {
      flexDirection: 'row',
      gap: s.s5,
      justifyContent: 'flex-start',
    },
    bodyMapWrap: {
      alignItems: 'center',
      paddingVertical: s.s2,
    },
    altsSection: { gap: s.s2 },
    altsLabel: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontWeight: '600',
      letterSpacing: 0.6,
      textTransform: 'uppercase',
    },
    altsEmpty: {
      color: p.ink2,
      fontSize: ty.sizes.sm,
      fontStyle: 'italic',
    },
    altRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: s.s3,
      paddingHorizontal: s.s4,
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r2,
      minHeight: 44,
    },
    altRowPressed: {
      backgroundColor: p.surface2,
    },
    altLabel: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s3,
      flex: 1,
    },
    altName: {
      color: p.ink,
      fontSize: ty.sizes.sm,
      fontWeight: '500',
      flex: 1,
    },
    altChevron: {
      color: p.ink3,
      fontSize: ty.sizes.md,
      marginLeft: s.s3,
    },
  });
}
